import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export const ICON_STYLE_STORAGE_KEY = 'prologue.icon-style';
export type IconStyle = 'maitian' | 'standard-svg';

function isIconStyle(value: string | null): value is IconStyle {
  return value === 'maitian' || value === 'standard-svg';
}

export function readStoredIconStyle(): IconStyle {
  if (typeof window === 'undefined') return 'maitian';
  const stored = window.localStorage.getItem(ICON_STYLE_STORAGE_KEY);
  return isIconStyle(stored) ? stored : 'maitian';
}

export interface IconStyleContextValue {
  iconStyle: IconStyle;
  setIconStyle: (style: IconStyle) => void;
}

const defaultContext: IconStyleContextValue = {
  iconStyle: 'maitian',
  setIconStyle: () => undefined,
};

export const IconStyleContext = createContext<IconStyleContextValue>(defaultContext);

export const IconStyleProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [iconStyle, setIconStyleState] = useState<IconStyle>(readStoredIconStyle);

  const setIconStyle = (next: IconStyle): void => {
    setIconStyleState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(ICON_STYLE_STORAGE_KEY, next);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.localStorage.setItem(ICON_STYLE_STORAGE_KEY, iconStyle);
    const handleStorage = (event: StorageEvent): void => {
      if (event.key !== ICON_STYLE_STORAGE_KEY) return;
      if (!isIconStyle(event.newValue)) return;
      setIconStyleState(event.newValue);
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [iconStyle]);

  const value = useMemo(() => ({ iconStyle, setIconStyle }), [iconStyle]);
  return <IconStyleContext.Provider value={value}>{children}</IconStyleContext.Provider>;
};

export function useIconStyle(): IconStyleContextValue {
  return useContext(IconStyleContext);
}
